import { TRADE_SPECIFIC_CELL_KEYS, TRADE_SPECIFIC_QUESTION_KEYS } from './questionConstants';
import {
  getQuestionByKey,
  getUserResponse,
  didUserSelectOptionWithAnswerByKey,
  didUserSelectAnyOptionWithAnswersByKey,
} from './scoreHelpers';
import { CareerUserResponseSet, CareerQuestion } from '../models/models';

export function determineIfTradeRecommended(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  if (!responseSet || !questions) {
    return false;
  }

  const cell_L5 = determineIfTradeRecommended_Cell_L5(responseSet, questions);
  const cell_M5 = determineIfTradeRecommended_Cell_M5(responseSet, questions);

  return cell_L5 || cell_M5;
}

/** Calculates Cell L5 function */
function determineIfTradeRecommended_Cell_L5(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  const enjoyActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_ENJOY,
  );
  const dislikeActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE,
  );

  const nextStepsQuestion = getQuestionByKey(questions, TRADE_SPECIFIC_QUESTION_KEYS.NEXT_STEPS);

  // here we're just looking to confirm that the questions exist, not necessarily that the user has answered them
  if (!enjoyActivitiesQuestion || !dislikeActivitiesQuestion || !nextStepsQuestion) {
    return false;
  }

  // Get user responses
  const enjoyActivitiesResponse = getUserResponse(responseSet, enjoyActivitiesQuestion.id);
  const dislikeActivitiesResponse = getUserResponse(responseSet, dislikeActivitiesQuestion.id);
  const nextStepsResponse = getUserResponse(responseSet, nextStepsQuestion.id);

  if (!enjoyActivitiesResponse || !dislikeActivitiesResponse || !nextStepsResponse) {
    return false;
  }

  // Check if the user enjoys any of the specified activities
  const targetActivities = [
    TRADE_SPECIFIC_CELL_KEYS.cell_D53,
    TRADE_SPECIFIC_CELL_KEYS.cell_D56,
    TRADE_SPECIFIC_CELL_KEYS.cell_D61,
  ];

  const enjoysAny = didUserSelectAnyOptionWithAnswersByKey({
    response: enjoyActivitiesResponse,
    question: enjoyActivitiesQuestion,
    answerTexts: targetActivities,
  });

  // Check if the user dislikes any of the specified activities
  const dislikesAny = didUserSelectAnyOptionWithAnswersByKey({
    response: dislikeActivitiesResponse,
    question: dislikeActivitiesQuestion,
    answerTexts: targetActivities,
  });

  // Check if the user chose 'Pursue Bachelors or 4-year degree'
  const choseVerySelectiveCollege = didUserSelectOptionWithAnswerByKey({
    response: nextStepsResponse,
    question: nextStepsQuestion,
    answerText: TRADE_SPECIFIC_CELL_KEYS.cell_D77,
  });

  // Final decision logic
  return enjoysAny && !dislikesAny && !choseVerySelectiveCollege;
}

/** Calculates Cell M5 function */
function determineIfTradeRecommended_Cell_M5(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  // Find questions
  const dislikeActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE,
  );
  const nextStepsQuestion = getQuestionByKey(questions, TRADE_SPECIFIC_QUESTION_KEYS.NEXT_STEPS);

  // here we're just looking to confirm that the questions exist, not necessarily that the user has answered them
  if (!dislikeActivitiesQuestion || !nextStepsQuestion) {
    return false;
  }

  /** DISLIKE ACTIVITIES QUESTION  */
  // Get user responses for dislikes
  const dislikeActivitiesResponse = getUserResponse(responseSet, dislikeActivitiesQuestion.id);

  if (!dislikeActivitiesResponse) {
    return false;
  }

  const targetDislikeActivities = [
    TRADE_SPECIFIC_CELL_KEYS.cell_D65,
    TRADE_SPECIFIC_CELL_KEYS.cell_D68,
    TRADE_SPECIFIC_CELL_KEYS.cell_D73,
  ];

  // Check if the user dislikes any of the specified activities
  const dislikesAny = didUserSelectAnyOptionWithAnswersByKey({
    response: dislikeActivitiesResponse,
    question: dislikeActivitiesQuestion,
    answerTexts: targetDislikeActivities,
  });

  /** EDUCATIONAL CHOICES / NEXT STEPS QUESTION */

  // Get user responses for educational choices ( i.e. next steps )
  const nextStepsResponse = getUserResponse(responseSet, nextStepsQuestion.id);

  const choseTradeSchool =
    nextStepsResponse &&
    didUserSelectOptionWithAnswerByKey({
      response: nextStepsResponse,
      question: nextStepsQuestion,
      answerText: TRADE_SPECIFIC_CELL_KEYS.cell_D78,
    });

  const didChooseTradeSchool = !!choseTradeSchool;

  /** FINAL DECISION LOGIC
   *
   * the user must have chosen trade school or apprenticeship program, and not dislike any of the specified activities
   */
  return didChooseTradeSchool && !dislikesAny;
}
